import React from 'react'

import {
  BarcodeIcon,
  MetaText,
  Button,
  CTALink,
  HeadingLarge,
  HeadingMedium,
  HeadingSmall,
  IconButton,
  Label,
  ProgressBar,
  QRCodeIcon,
  SettingsIcon,
} from '@pb/components'
import { getLabelFromStorage } from '@pb/utils'
import cn from 'classnames'
import CountUp from 'react-countup'
import { useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'

import { ACCOUNT_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import { Inbox } from 'features/account/inbox'
import pointsSelector from 'features/account/points-history-legacy/duck/selectors'

import cardSelectors from '../../duck/selectors'
import styles from './CardFront.scss'
import { getPercentageToFill } from './helpers'
import Props from './types'

const CardFront: React.FC<Props> = ({
  actionToggleClubCard,
  name,
  currentBalance,
  isEntryPointBannerActive,
}) => {
  const nextTierCopy = useSelector(cardSelectors.getNextTierCopy)
  const mainMembershipCardCopy = useSelector(cardSelectors.getMainMembershipCardCopy)
  const isPlusMember = useSelector(cardSelectors.getIsPlusMember)
  const currentTierCopy = useSelector(cardSelectors.getCurrentTierCopy)
  const nextTierBalance = useSelector(cardSelectors.getNextTierBalance) || 1
  const bonusVoucherPointsInterval = useSelector(appSelectors.getBonusVoucherPointsInterval) || 1
  const bonusMilestonesInPercentage =
    isPlusMember && currentBalance >= nextTierBalance
      ? 100
      : Math.min(bonusVoucherPointsInterval / nextTierBalance, 1) * 100
  const maxNumberOfBonusVouchersPerYear = useSelector(
    appSelectors.getMaxNumberOfBonusVouchersPerYear,
  )
  const hasReachedBonusVoucherLimit =
    !!maxNumberOfBonusVouchersPerYear &&
    currentBalance / bonusVoucherPointsInterval >= maxNumberOfBonusVouchersPerYear
  const percentageToFill = getPercentageToFill({
    isPlusMember,
    currentBalance,
    nextTierBalance,
    hasReachedBonusVoucherLimit,
    bonusVoucherPointsInterval,
  })
  const shouldRenderCounter = useSelector(cardSelectors.getShouldRenderCounter)
  const isCodeTypeBarcode = useSelector(cardSelectors.getIsCodeTypeBarcode)
  const isPointsHistoryEnabled = useSelector(appSelectors.getIsPointsHistoryEnabled)
  const isConsciousPointsActive = useSelector(pointsSelector.getIsConsciousPointsActive)

  const history = useHistory()
  const pointsLabel = getLabelFromStorage('points.label.copy')
  const navigateToPointsHistoryPage = (): void => {
    if (isPointsHistoryEnabled) {
      history.push(ACCOUNT_ROUTES.PointsHistory?.path)
    }
  }

  return (
    <>
      <div className={styles.cardHeader}>
        <HeadingMedium Tag="h2" className={styles.name}>
          <Label copyKey="club.full.reminder.popup.hello" replacements={name || currentTierCopy} />
        </HeadingMedium>
        <span className={styles.iconContainer}>
          <Inbox />
          <IconButton
            Tag={Link}
            to={ACCOUNT_ROUTES.Settings.path}
            aria-label={getLabelFromStorage(ACCOUNT_ROUTES.Settings.title)}
            iconElement={<SettingsIcon />}
          />
        </span>
      </div>
      <div
        className={cn(
          styles.container,
          isEntryPointBannerActive && styles.containerWithoutMarginBottom,
        )}
      >
        {isPlusMember && (
          <HeadingSmall className={styles.plusMemberLabel}>{currentTierCopy}</HeadingSmall>
        )}
        <ProgressBar
          onClick={navigateToPointsHistoryPage}
          percentage={percentageToFill}
          color="member"
          thickness="medium"
          progressBarStartBullet={isPlusMember}
          milestonePercentageInterval={bonusMilestonesInPercentage}
          delayInitialAnimation
          className={isPointsHistoryEnabled ? styles.hover : ''}
          progressBarLabel={
            <>
              <HeadingLarge Tag="span" className={styles.currentBalance}>
                {shouldRenderCounter ? (
                  <CountUp
                    start={0}
                    end={currentBalance}
                    delay={1}
                    suffix={` ${pointsLabel}`}
                    duration={1}
                  />
                ) : (
                  <>0 {pointsLabel}</>
                )}
              </HeadingLarge>
              {isConsciousPointsActive && (
                <CTALink
                  className={styles.pointsCTA}
                  Tag={Link}
                  to={ACCOUNT_ROUTES.PointsHistory?.path}
                  inline
                >
                  <MetaText>{getLabelFromStorage('general.conscious.points')}</MetaText>
                </CTALink>
              )}
            </>
          }
        />
        <MetaText Tag="p" className={styles.nextTierText}>
          {mainMembershipCardCopy || nextTierCopy}
        </MetaText>
        <Button
          secondary
          fullWidth
          iconPosition="start"
          iconElement={
            isCodeTypeBarcode ? (
              <BarcodeIcon className={styles.icon} />
            ) : (
              <QRCodeIcon className={styles.icon} />
            )
          }
          onClick={actionToggleClubCard}
        >
          <Label copyKey="view.your.member.id" />
        </Button>
      </div>
    </>
  )
}

export default CardFront
